import { useEffect } from 'react';
import getTrimWithInteriorColorPricing from '../../../utils/getTrimWithInteriorColorPricing';
import useAllTransmissionsPayments from './useAllTransmissionsPayments';
import useInventoryStatusAndPrice from './useInventoryStatusAndPrice';
import useProductFinancial from '../../../apiHooks/useProductFinancial';
import { InventoryAvailabilityStatus } from '../../../core/apis/inventoryApi';
import { selectCurrentModelFilteredVehicles, selectCurrentModelVehiclesByTransmissionAndColor, selectFilteredVehiclesFromDealerInventory, } from '../../../core/reducers/vehicleListFilters';
import { isItemIncludedForBAP, useAppSelector } from '../../../core/utils';
import { useUserOrDefaultProvince } from '../../../utils/sitecoreContext';
const getTransmission = (trim, transmissionKey) => {
    const transmission = trim.transmissions.find(t => t.transmissionKey === transmissionKey);
    return transmission;
};
const getPriceByTransmission = (trim, payments, financial, hasError, isPaymentFetching) => payments === null || payments === void 0 ? void 0 : payments.filter(payment => {
    const { detKeys } = payment || {};
    const { exteriorColorKey, interiorColorKey, modelKey, modelYear, transmissionKey, trimKey } = detKeys || {};
    const transmission = getTransmission(trim, transmissionKey);
    const hasInvalidInteriorPricing = !getTrimWithInteriorColorPricing(financial, trim, hasError, exteriorColorKey, transmissionKey, interiorColorKey);
    if (hasInvalidInteriorPricing && !isPaymentFetching) {
        return null;
    }
    return (modelKey === trim.modelKey &&
        modelYear === trim.modelYear &&
        trimKey === trim.trimKey &&
        transmissionKey === (transmission === null || transmission === void 0 ? void 0 : transmission.transmissionKey) &&
        exteriorColorKey === (transmission === null || transmission === void 0 ? void 0 : transmission.exteriorColorKey) &&
        interiorColorKey === (transmission === null || transmission === void 0 ? void 0 : transmission.interiorColorKey));
});
const getTrimWithPaymentData = (trim, paymentsResponse) => {
    var _a, _b, _c;
    const { trimKey, transmissions, defaultTransmission } = trim;
    const transmissionKey = defaultTransmission;
    const exteriorColorKey = (_a = transmissions[0]) === null || _a === void 0 ? void 0 : _a.exteriorColorKey;
    const interiorColorKey = (_b = transmissions[0]) === null || _b === void 0 ? void 0 : _b.interiorColorKey;
    const trimPayment = (_c = paymentsResponse === null || paymentsResponse === void 0 ? void 0 : paymentsResponse.data) === null || _c === void 0 ? void 0 : _c.find(payment => {
        const { detKeys } = payment || {};
        return (trimKey === (detKeys === null || detKeys === void 0 ? void 0 : detKeys.trimKey) &&
            transmissionKey === (detKeys === null || detKeys === void 0 ? void 0 : detKeys.transmissionKey) &&
            exteriorColorKey === (detKeys === null || detKeys === void 0 ? void 0 : detKeys.exteriorColorKey) &&
            interiorColorKey === (detKeys === null || detKeys === void 0 ? void 0 : detKeys.interiorColorKey));
    });
    return {
        ...trim,
        paymentData: {
            data: [trimPayment],
            error: paymentsResponse.error,
            isFetching: paymentsResponse.isFetching,
            isUninitialized: paymentsResponse.isFetching,
        },
    };
};
const sortByPrice = (a, b) => {
    var _a, _b;
    // Sort low to high; missing payment data at the end
    if (!a.paymentData.data || !b.paymentData.data)
        return 1;
    const aPrice = ((_a = a.paymentData.data[0]) === null || _a === void 0 ? void 0 : _a.msrp.value) || 0;
    const bPrice = ((_b = b.paymentData.data[0]) === null || _b === void 0 ? void 0 : _b.msrp.value) || 0;
    return aPrice - bPrice;
};
const useFilteredCardsByVariant = (isInventoryVariant, hasModelData, selectedModelKey, selectedModelYear) => {
    const provinceCode = useUserOrDefaultProvince();
    let defaultFilteredTrims = useAppSelector(selectCurrentModelFilteredVehicles);
    defaultFilteredTrims = defaultFilteredTrims.filter(trim => isItemIncludedForBAP(trim, provinceCode));
    const paymentResponse = useAllTransmissionsPayments(!isInventoryVariant && hasModelData);
    const { financial, setModels, hasError, isFetching: isFinancialsFetching, } = useProductFinancial({});
    useEffect(() => {
        const financialModels = defaultFilteredTrims.map(trim => ({
            modelKey: trim.modelKey,
            modelYear: trim.modelYear,
        }));
        setModels(financialModels);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedModelKey, selectedModelYear, defaultFilteredTrims.length]);
    const filteredTrimsWithPayment = defaultFilteredTrims.map(trim => {
        const paymentData = getPriceByTransmission(trim, paymentResponse === null || paymentResponse === void 0 ? void 0 : paymentResponse.data, financial, hasError, paymentResponse.isFetching);
        return {
            ...trim,
            paymentData: {
                data: paymentData,
                error: paymentResponse.error,
                isFetching: paymentResponse.isFetching || isFinancialsFetching,
                isUninitialized: paymentResponse.isUninitialized,
            },
        };
    });
    /**
     * Inventory variant.
     */
    const vehiclesByTransmissionAndColor = useAppSelector(selectCurrentModelVehiclesByTransmissionAndColor);
    const shouldFetchInventory = isInventoryVariant && Array.isArray(vehiclesByTransmissionAndColor) && vehiclesByTransmissionAndColor.length > 0;
    const { dealerInventory, isFetching, isUninitialized, error, paymentsResponse: inventoryPaymentResponse, } = useInventoryStatusAndPrice(shouldFetchInventory && hasModelData);
    const filteredDealerInventory = useAppSelector(state => selectFilteredVehiclesFromDealerInventory(state, dealerInventory));
    // Cards with payment data, sorted by price, with inventory status prop (for the badge)
    const inStockCards = filteredDealerInventory.inStock
        .map(trim => getTrimWithPaymentData(trim, inventoryPaymentResponse))
        .sort(sortByPrice)
        .map(v => ({
        ...v,
        inventoryStatus: InventoryAvailabilityStatus.inStock,
    }));
    const onItsWayCards = filteredDealerInventory.onItsWay
        .map(trim => getTrimWithPaymentData(trim, inventoryPaymentResponse))
        .sort(sortByPrice)
        .map(v => ({
        ...v,
        inventoryStatus: InventoryAvailabilityStatus.onItsWay,
    }));
    // inStock first, then onItsWay
    if (isInventoryVariant) {
        return { trimCards: [...inStockCards, ...onItsWayCards], isFetching, isUninitialized, error };
    }
    /**
     * Default variant.
     */
    return { trimCards: filteredTrimsWithPayment, isFetching, isUninitialized, error };
};
export default useFilteredCardsByVariant;
